import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ImageDocument {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  uploaded_at: string;
  signedUrl?: string;
}

interface ImageGalleryProps {
  images: ImageDocument[];
}

export const ImageGallery = ({ images }: ImageGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const { toast } = useToast();

  const loadImageUrl = async (image: ImageDocument) => {
    if (imageUrls.has(image.id)) return imageUrls.get(image.id);

    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(image.file_path, 3600);

      if (error) throw error;

      setImageUrls(prev => new Map(prev).set(image.id, data.signedUrl));
      return data.signedUrl;
    } catch (error: any) {
      console.error("Failed to load image:", error);
      return null;
    }
  };

  const handleImageClick = async (index: number) => {
    await loadImageUrl(images[index]);
    setSelectedIndex(index);
  };

  const handlePrevious = async () => {
    if (selectedIndex === null) return;
    const newIndex = selectedIndex > 0 ? selectedIndex - 1 : images.length - 1;
    await loadImageUrl(images[newIndex]);
    setSelectedIndex(newIndex);
  };

  const handleNext = async () => {
    if (selectedIndex === null) return;
    const newIndex = selectedIndex < images.length - 1 ? selectedIndex + 1 : 0;
    await loadImageUrl(images[newIndex]);
    setSelectedIndex(newIndex);
  };

  const handleDownload = async () => {
    if (selectedIndex === null) return;
    const image = images[selectedIndex];

    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(image.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = image.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        description: image.file_name,
      });
    } catch (error: any) {
      toast({
        title: "Download failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Load initial URLs for gallery thumbnails
  useState(() => {
    images.forEach(image => loadImageUrl(image));
  });

  if (images.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No images to display
      </div>
    );
  }

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  const selectedImageUrl = selectedImage ? imageUrls.get(selectedImage.id) : null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((image, index) => {
          const url = imageUrls.get(image.id);
          return (
            <div
              key={image.id}
              className="aspect-square rounded-lg overflow-hidden border border-border hover:border-primary cursor-pointer transition-colors group relative"
              onClick={() => handleImageClick(index)}
            >
              {url ? (
                <>
                  <img
                    src={url}
                    alt={image.file_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <p className="text-white text-sm text-center px-2 truncate">
                      {image.file_name}
                    </p>
                  </div>
                </>
              ) : (
                <div className="w-full h-full bg-muted animate-pulse" />
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0">
          <div className="relative w-full h-full flex items-center justify-center bg-black">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
              onClick={() => setSelectedIndex(null)}
            >
              <X className="h-6 w-6" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-16 z-10 text-white hover:bg-white/20"
              onClick={handleDownload}
            >
              <Download className="h-6 w-6" />
            </Button>

            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                  onClick={handlePrevious}
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                  onClick={handleNext}
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              </>
            )}

            {selectedImageUrl && (
              <img
                src={selectedImageUrl}
                alt={selectedImage?.file_name}
                className="max-w-full max-h-[85vh] object-contain"
              />
            )}

            {selectedImage && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
                <p className="text-center font-medium">{selectedImage.file_name}</p>
                {images.length > 1 && (
                  <p className="text-center text-sm text-gray-300">
                    {selectedIndex! + 1} of {images.length}
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
